import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Flash message controller with auto-dismiss and animations
// Connects to flash messages and handles:
// - Auto-dismiss for success/info messages (5 seconds)
// - Manual dismiss for error/warning/danger messages
// - Smooth animations (slide-down entrance, fade-out exit)
// - Keyboard accessibility (ESC to close)
export default class extends Controller {
  static values = {
    type: String,
    autoDismiss: { type: Boolean, default: true }
  }

  connect() {
    logger.log('Flash controller connected', this.element)

    // Add entrance animation
    this.element.classList.add('flash-enter')

    // Remove entrance animation class after animation completes
    setTimeout(() => {
      this.element.classList.remove('flash-enter')
    }, 300)

    // Auto-dismiss logic based on message type
    if (this.shouldAutoDismiss()) {
      this.dismissTimeout = setTimeout(() => {
        this.dismiss()
      }, 5000) // 5 seconds
    }

    // Add keyboard listener for ESC key
    this.escapeHandler = (event) => {
      if (event.key === 'Escape') {
        this.dismiss()
      }
    }
    document.addEventListener('keydown', this.escapeHandler)
  }

  disconnect() {
    // Clean up timeout and event listener
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout)
    }
    document.removeEventListener('keydown', this.escapeHandler)
  }

  // Determine if message should auto-dismiss
  shouldAutoDismiss() {
    if (!this.autoDismissValue) {
      return false
    }

    const type = this.typeValue.toLowerCase()
    // Auto-dismiss success, notice, and info messages
    // Keep error, alert, warning, and danger messages until manually closed
    return ['success', 'notice', 'info'].includes(type)
  }

  // Dismiss message with fade-out animation
  dismiss() {
    logger.log('Dismissing flash message', this.element)

    // Add fade-out animation
    this.element.classList.add('flash-exit')

    // Remove element after animation completes
    setTimeout(() => {
      this.element.remove()
    }, 300)
  }

  // Manual close button click handler
  close(event) {
    event.preventDefault()
    this.dismiss()
  }
}
